import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { House, FilePlus, BookOpen, ClipboardText, SignOut, Sparkle, UsersThree, Question, Palette, List, X, Plug, UserCircle, Signature, ChartLineUp, Funnel, Kanban, AddressBook, FileText, Robot, Ticket, PaperPlaneTilt, Users } from "@phosphor-icons/react";
import NotificationBell from "@/components/NotificationBell";

const NavItem = ({ to, icon: Icon, label, testid, onClick }) => (
  <NavLink
    to={to}
    data-testid={testid}
    end={to === "/"}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 text-sm font-medium border-l-2 transition-colors duration-150 ${
        isActive
          ? "border-blue-600 bg-blue-50 text-slate-900"
          : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon size={18} weight="regular" />
    <span>{label}</span>
  </NavLink>
);

const RoleBadge = ({ role }) => {
  const c = { admin: "bg-slate-900 text-white", sales: "bg-blue-100 text-blue-800", approver: "bg-emerald-100 text-emerald-800" }[role] || "bg-slate-200";
  return <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-sm ${c}`}>{role}</span>;
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      {/* Mobile topbar */}
      <div className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3 sticky top-0 z-30" data-testid="mobile-topbar">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu" className="p-1 -ml-1">
          <List size={22} weight="bold"/>
        </button>
        <div className="flex items-center gap-2">
          <img src="/tn-icon.png" alt="thinkneural.ai" className="h-6 w-6"/>
          <span className="font-display font-black text-sm tracking-tight">thinkneural<span className="text-blue-600">.ai</span></span>
        </div>
        <NotificationBell />
      </div>

      {/* Overlay */}
      {open && <button aria-label="Close menu" onClick={close} className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden" data-testid="mobile-overlay"/>}

      <div className="lg:flex">
        <aside
          className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-50 w-64 h-screen bg-white border-r border-slate-200 flex flex-col transition-transform duration-200 ease-out`}
          data-testid="sidebar"
        >
          <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img src="/tn-icon.png" alt="thinkneural.ai" className="h-9 w-9"/>
              <div>
                <div className="font-display font-black text-[14px] leading-none tracking-tight">thinkneural<span className="text-blue-600">.ai</span></div>
                <div className="label-micro mt-1">Sales Suite</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <div className="hidden lg:block"><NotificationBell /></div>
              <button onClick={close} className="lg:hidden p-1 -mr-1" aria-label="Close menu" data-testid="sidebar-close">
                <X size={18}/>
              </button>
            </div>
          </div>

          <nav className="flex-1 py-4 flex flex-col gap-0.5 overflow-y-auto">
            <div className="label-micro px-4 pb-2">Workspace</div>
            <NavItem to="/" icon={House} label="Dashboard" testid="nav-dashboard" onClick={close}/>
            <NavItem to="/proposals" icon={FileText} label="Proposals" testid="nav-proposals" onClick={close}/>
            <NavItem to="/revenue" icon={ChartLineUp} label="Revenue tracker" testid="nav-revenue" onClick={close}/>
            <NavItem to="/pipeline" icon={Funnel} label="Pipeline summary" testid="nav-pipeline" onClick={close}/>
            <NavItem to="/crm" icon={AddressBook} label="Customer records" testid="nav-crm" onClick={close}/>
            <NavItem to="/team" icon={Users} label="Team overview" testid="nav-team" onClick={close}/>
            <NavItem to="/tickets" icon={Ticket} label="Tickets" testid="nav-tickets" onClick={close}/>
            <NavItem to="/outreach" icon={PaperPlaneTilt} label="Outreach" testid="nav-outreach" onClick={close}/>
            <NavItem to="/connect-claude" icon={Robot} label="Connect Claude" testid="nav-connect-claude" onClick={close}/>
            <div className="label-micro px-4 pt-4 pb-2">Content</div>
            <NavItem to="/library" icon={BookOpen} label="Libraries" testid="nav-libraries" onClick={close}/>
            <NavItem to="/documents" icon={Signature} label="Documents" testid="nav-documents" onClick={close}/>
            <NavItem to="/profile" icon={UserCircle} label="My Profile" testid="nav-profile" onClick={close}/>
            <NavItem to="/audit" icon={ClipboardText} label="Audit Log" testid="nav-audit" onClick={close}/>
            <NavItem to="/help" icon={Question} label="Help & Tutorials" testid="nav-help" onClick={close}/>
            {isAdmin && (
              <>
                <div className="label-micro px-4 pt-4 pb-2">Admin</div>
                <NavItem to="/users" icon={UsersThree} label="Users & Roles" testid="nav-users" onClick={close}/>
                <NavItem to="/branding" icon={Palette} label="Branding & Layout" testid="nav-branding" onClick={close}/>
                <NavItem to="/integrations" icon={Plug} label="Integrations" testid="nav-integrations" onClick={close}/>
              </>
            )}
          </nav>

          <div className="border-t border-slate-200 p-4">
            <div className="text-sm font-semibold text-slate-900" data-testid="current-user-name">{user?.full_name || "—"}</div>
            <div className="text-xs text-slate-500 mt-0.5 break-all">{user?.email}</div>
            <div className="mt-2 flex gap-1 flex-wrap" data-testid="current-user-roles">
              {(user?.roles || []).map(r => <RoleBadge key={r} role={r} />)}
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="mt-3 w-full flex items-center justify-center gap-2 text-xs font-semibold border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-900 hover:text-white transition-colors duration-150"
            >
              <SignOut size={14} />
              Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
